import React, { Fragment } from "react";

import Checkbox from "@material-ui/core/Checkbox";
import MaUTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TablePaginationActions from "./TablePaginationActions";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import TableToolbar from "./TableToolbar";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField";
import "./style.css";
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from "react-table";

import { withStyles, makeStyles } from "@material-ui/core/styles";

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  body: {
    fontSize: 11
  }
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.background.default
    }
  }
}))(TableRow);

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <Checkbox size="medium" ref={resolvedRef} {...rest} />
      </>
    );
  }
);

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto"
  },
  table: {
    minWidth: 700
  }
}));

const inputStyle = {
  padding: 0,
  margin: 0,
  border: 0,
  background: "transparent",
  cursor: "pointer"
};

// Create an editable cell renderer
const EditableCell = ({
  cell: { value: initialValue },
  row: { index },
  column: { id },
  updateMyData,
  fields // This is a custom function that we supplied to our table instance
}) => {
  // We need to keep and update the state of the cell normally
  const [value, setValue] = React.useState(initialValue);
  const [disabled, setDisabled] = React.useState(true);

  const onChange = e => {
    setValue(e.target.value);
  };

  const inputRef = React.createRef();

  console.log("Index", index, "Id", id, "Value", value);

  // We'll only update the external data when the input is blurred
  const onBlur = () => {
    updateMyData(index, id, value);
    setDisabled(true);
  };

  const handleChange = () => {
    updateMyData(index, id, !value);
  };

  const handleHide = v => {
    setDisabled(false);
    // inputRef.current.focus();
  };

  // If the initialValue is changed externall, sync it up with our state
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  if (initialValue === null) {
    return <></>;
  } else if (initialValue === true || initialValue === false) {
    return (
      <Checkbox
        checked={initialValue}
        value={value}
        onBlur={onBlur}
        onChange={handleChange}
        inputProps={{ "aria-label": "primary checkbox" }}
      />
      // <input
      //   type="checkbox"
      //   checked={initialValue}
      //   value={value}
      //   onChange={handleChange}
      // ></input>
    );
  } else {
    return (
      <div className="container">
        {disabled === true ? (
          <p style={{ marginTop: "10px" }}>{value}</p>
        ) : (
          <TextField
            style={inputStyle}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            autoFocus
          />
        )}

        <div style={{ marginTop: "8px", marginLeft: "12px" }}>
          <span className="material-icons" onClick={handleHide}>
            edit
          </span>
        </div>
      </div>
    );
  }
};

EditableCell.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.any.isRequired
  }),
  row: PropTypes.shape({
    index: PropTypes.number.isRequired
  }),
  column: PropTypes.shape({
    id: PropTypes.number.isRequired
  }),
  updateMyData: PropTypes.func.isRequired,
  fields: PropTypes.any.isRequired
};

// Set our editable cell renderer as the default Cell renderer
const defaultColumn = {
  Cell: EditableCell
};

const EnhancedTable = ({
  createData,
  deleteData,
  columns,
  data,
  setData,
  updateMyData,
  skipPageReset,
  fields
}) => {
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    selectedFlatRows,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter }
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      autoResetPage: !skipPageReset,
      // updateMyData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateMyData,
      fields
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      fields !== undefined &&
        hooks.allColumns.push(columns => [
          // Let's make a column for selection
          {
            id: "selection",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox.  Pagination is a problem since this will select all
            // rows even though not all rows are on the current page.  The solution should
            // be server side pagination.  For one, the clients should not download all
            // rows in most cases.  The client should only download data for the current page.
            // In that case, getToggleAllRowsSelectedProps works fine.
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div >
                <IndeterminateCheckbox  {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },

          ...columns
        ]);
    }
  );

  const handleChangePage = (event, newPage) => {
    gotoPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setPageSize(Number(event.target.value));
  };

  const [select, selectRow] = React.useState([]);

  React.useEffect(() => {
    let a = selectedFlatRows.map(row => row.original);
    selectRow(a);
  }, [selectRow, selectedFlatRows]);

  const removeByIndexs = (array, indexs) =>
    array.filter((_, i) => !indexs.includes(i));

  const deleteHandler = e => {
    if (window.confirm("Do you really want to delete?")) {
      select.map(item => deleteData(item._id));
      const newData = removeByIndexs(
        data,
        Object.keys(selectedRowIds).map(x => parseInt(x, 10))
      );
      setData(newData);
    }
  };

  const addHandler = async user => {
    console.log(user);
    let res = await createData(user);
    const newData = data.concat([res.data.response]);
    setData(newData);
  };

  const classes = useStyles();

  // Render the UI for your table
  return (
    <>
      <TableContainer>
        {fields !== undefined && (
          <>
            <TableToolbar
              numSelected={Object.keys(selectedRowIds).length}
              deleteUserHandler={deleteHandler}
              addUserHandler={addHandler}
              fields={fields}
              preGlobalFilteredRows={preGlobalFilteredRows}
              setGlobalFilter={setGlobalFilter}
              globalFilter={globalFilter}
            />
          </>
        )}

        <MaUTable {...getTableProps()}>
          <TableHead>
            {headerGroups.map(headerGroup => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <StyledTableCell
                    width={100}
                    style={{ height: "20px" , width : '20px' }}
                    {...(column.id === "selection"
                      ? column.getHeaderProps()
                      : column.getHeaderProps(column.getSortByToggleProps()))}
                  >
                    {column.render("Header")}
                    {/* {column.id !== "selection" ? (
                      <TableSortLabel
                        active={column.isSorted}
                        // react-table has a unsorted state which is not treated here
                        direction={column.isSortedDesc ? "desc" : "asc"}
                      />
                    ) : null} */}
                  </StyledTableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <StyledTableRow {...row.getRowProps()}>
                  {row.cells.map(cell => {
                    return (
                      <StyledTableCell {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              );
            })}
          </TableBody>
        </MaUTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, { label: "All", value: data.length }]}
        colSpan={3}
        count={data.length}
        rowsPerPage={pageSize}
        page={pageIndex}
        SelectProps={{
          inputProps: { "aria-label": "rows per page" },
          native: true
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </>
  );
};

EnhancedTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  updateMyData: PropTypes.func.isRequired,
  setData: PropTypes.func.isRequired,
  skipPageReset: PropTypes.bool.isRequired
};

export default EnhancedTable;
